import React from 'react';
import { connect, useSelector } from 'react-redux';
import RedirectToProductList from './components/redirectToProductList';
import MACPageHeader from './components/macPageHeader';
import { State } from '../../model/State';
import { selectLicenseDetails } from './duck/selectors/license';
import { FormattedMessage } from 'react-intl';
import { messages } from './messages';
import styled from 'styled-components';
import { colors } from '@atlaskit/theme';
import { Dimensions, Font } from '../../components/visuals';
import TextArea from '@atlaskit/textarea';
import Button from '@atlaskit/button';
import { PageH3Header } from './components/styledComponents';

const SectionTitle = styled.h3`
  color: ${colors.N800};
  font-size: ${Dimensions.units(2.5)}px;
  margin-top: ${Dimensions.units(3.75)}px !important;
  margin-bottom: ${Dimensions.units(2)}px;
  font-weight: 500;
  font-family: ${Font.displayFamily};
  letter-spacing: 0.3px;
`;

const DownloadButton = styled(Button)`
  margin-top: ${Dimensions.units(1)}px;
`;

const List = styled.ul`
  padding-left: ${Dimensions.units(2.5)}px;
`;

const getLink = (productKeyWithoutPrefix: string, isDataCenter: boolean): string => {
  let link: string;
  if (isDataCenter) {
    const trimmedKey = productKeyWithoutPrefix.substring(
      0,
      productKeyWithoutPrefix.length - '.data-center'.length,
    );
    link = `https://marketplace.atlassian.com/download/plugins/${trimmedKey}?hosting=datacenter`;
  } else {
    link = `https://marketplace.atlassian.com/download/plugins/${productKeyWithoutPrefix}?hosting=server`;
  }
  return link;
};

const AddonLicenseViewPage = () => {
  const licenseId = useSelector((state) => (state as State).location.payload.licenseId);
  const licenseDetails = useSelector((state) => selectLicenseDetails(state as State, licenseId));
  const addOn = licenseDetails?.productPackageDetails;
  const productKey = addOn?.key?.replace('pricingplan.', '');
  const link = getLink(productKey, addOn?.isDataCenter);

  return !licenseDetails ? null : (
    <>
      <RedirectToProductList />
      <MACPageHeader />
      <PageH3Header>
        <FormattedMessage {...messages.addonLicenseViewPageTitle} />
      </PageH3Header>
      <hr />

      <SectionTitle>
        <FormattedMessage {...messages.addonLicenseKeyInfoTitle} />
      </SectionTitle>
      <TextArea
        id="area"
        maxHeight="30vh"
        name="area"
        value={licenseDetails?.licenseDetails?.license}
      />
      <SectionTitle>
        <FormattedMessage {...messages.addonDownloadAppTitle} />
      </SectionTitle>

      <p>
        <FormattedMessage
          id="mac.license.addon.view.download.app.explanation"
          defaultMessage="Download the {description} app to your local system:"
          values={{
            description: <em>{licenseDetails?.productPackageDetails?.description}</em>,
          }}
        />
      </p>

      <DownloadButton href={link} appearance="primary">
        <FormattedMessage {...messages.addonDownloadButtonText} />
      </DownloadButton>

      <SectionTitle>
        <FormattedMessage {...messages.addonInstallAppTitle} />
      </SectionTitle>
      <p>
        <FormattedMessage
          id="mac.license.addon.view.install.app.note"
          defaultMessage="{note} You must have administrative rights to install an app."
          values={{
            note: (
              <strong>
                <FormattedMessage {...messages.addonNoteText} />
              </strong>
            ),
          }}
        />
      </p>

      <p style={{ marginTop: `${Dimensions.units(1)}px` }}>
        <List>
          <li>
            <FormattedMessage {...messages.addonInstallOpen} />
          </li>
          <li>
            <FormattedMessage {...messages.addonInstallNavigate} />
          </li>
          <li>
            <FormattedMessage {...messages.addonInstallChose} />
          </li>
          <li>
            <FormattedMessage {...messages.addonInstallUpload} />
          </li>
        </List>
      </p>

      <SectionTitle>
        <FormattedMessage {...messages.addonApplyLicenseTitle} />
      </SectionTitle>

      <p>
        <List>
          <li>
            <FormattedMessage {...messages.addonLicensesNavigate} />
          </li>
          <li>
            <FormattedMessage
              id="mac.license.addon.view.licenses.locate"
              defaultMessage="Locate your installed {productDescription} app in the list."
              values={{
                note: <em>{addOn?.description}</em>,
              }}
            />
          </li>

          <li>
            <FormattedMessage {...messages.addonLicensesCopy} />
          </li>
        </List>
      </p>
      <p style={{ marginTop: `${Dimensions.units(4)}px` }}>
        <a
          href="https://confluence.atlassian.com/x/eJgCDQ"
          target="_blank"
          rel="noopener noreferrer"
        >
          <FormattedMessage {...messages.addonLicenseLearnMore} />
        </a>
      </p>
    </>
  );
};

export const MACAddonLicenseViewPage = connect(null, null)(AddonLicenseViewPage);
